import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { SculptRunRequest } from "~/api/client";
import { SculptConfirmRequiredError } from "~/api/errors";
import { useSculpt } from "~/api/queries";
import type { SculptOpKind } from "~/api/types";
import { Button } from "~/foundation/components/Button";
import { Dialog } from "~/foundation/components/Dialog";
import { Switch } from "~/foundation/components/Switch";

/** The ops a person reaches for from the dialog; the exotic rest stay API-only. */
const DIALOG_OPS: SculptOpKind[] = [
  "insertSection",
  "writeSection",
  "outline",
  "restructure",
  "compose",
];

const OP_LABEL: Record<string, string> = {
  insertSection: "Add a section",
  writeSection: "Write a section",
  outline: "Outline the document",
  restructure: "Restructure",
  compose: "Rewrite everything",
};

export interface SculptDialogProps {
  documentId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Whether the viewer may apply directly; without it everything stages a proposal. */
  canUpdate: boolean;
}

/**
 * "Sculpt with AI" — one instruction, one op, one lane. Direct-apply is opt-in even for
 * editors: the reviewable propose lane is the default, and a destructive direct apply is
 * re-confirmed with the server's drift token before anything is replaced.
 */
export function SculptDialog({ documentId, open, onOpenChange, canUpdate }: SculptDialogProps) {
  const { t } = useTranslation();
  const sculpt = useSculpt();
  const [op, setOp] = useState<SculptOpKind>("insertSection");
  const [instructions, setInstructions] = useState("");
  const [direct, setDirect] = useState(false);
  const [confirm, setConfirm] = useState<{ token: string; summary: string } | null>(null);
  const idempotencyKey = useRef<string>("");

  const close = (next: boolean) => {
    if (!next) {
      setConfirm(null);
    }
    onOpenChange(next);
  };

  const finish = (status: string) => {
    if (status === "applied") {
      toast.success(t("Sculpt applied"));
    } else if (status === "degraded") {
      toast.warning(t("The document changed mid-sculpt — the rest is staged for review"));
    } else {
      toast.success(t("Proposal ready — review it on the document"));
    }
    setInstructions("");
    setConfirm(null);
    close(false);
  };

  const submit = (confirmToken?: string) => {
    if (!confirmToken) {
      idempotencyKey.current = crypto.randomUUID();
    }
    const request: SculptRunRequest = {
      op,
      instructions,
      requestedLane: direct && canUpdate ? "act" : "propose",
      idempotencyKey: idempotencyKey.current,
      ...(confirmToken ? { confirmToken } : {}),
    };
    sculpt.mutate(
      { documentId, request },
      {
        onSuccess: (outcome) => finish(outcome.status),
        onError: (error) => {
          if (error instanceof SculptConfirmRequiredError) {
            setConfirm({ token: error.confirmToken, summary: error.message });
            return;
          }
          toast.error(error instanceof Error ? error.message : t("Something went wrong"));
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={close}
      title={t("Sculpt with AI")}
      description={t("Describe the change; the AI works on the document's own structure.")}
      closeLabel={t("Close")}
      footer={
        confirm ? (
          <>
            <Button variant="ghost" onClick={() => setConfirm(null)}>
              {t("Back")}
            </Button>
            <Button
              variant="danger"
              disabled={sculpt.isPending}
              onClick={() => submit(confirm.token)}
            >
              {t("Replace content")}
            </Button>
          </>
        ) : (
          <>
            <Button variant="ghost" onClick={() => close(false)}>
              {t("Cancel")}
            </Button>
            <Button
              variant="primary"
              disabled={sculpt.isPending || instructions.trim().length === 0}
              onClick={() => submit()}
            >
              {sculpt.isPending ? t("Sculpting…") : t("Sculpt")}
            </Button>
          </>
        )
      }
    >
      {confirm ? (
        <p className="text-body text-text">
          {t(
            "This will replace existing content ({{scope}}). The current version is kept as a checkpoint you can revert to.",
            { scope: confirm.summary },
          )}
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-small text-text-secondary">
            {t("What should change?")}
            <select
              value={op}
              onChange={(event) => setOp(event.target.value as SculptOpKind)}
              className="h-8 rounded-md border border-input-border bg-bg px-2 text-body text-text focus:border-input-border-focus"
            >
              {DIALOG_OPS.map((value) => (
                <option key={value} value={value}>
                  {t(OP_LABEL[value] ?? value)}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1 text-small text-text-secondary">
            {t("Instructions")}
            <textarea
              value={instructions}
              onChange={(event) => setInstructions(event.target.value)}
              rows={4}
              placeholder={t("e.g. Add a section comparing the two rollout options")}
              className="resize-y rounded-md border border-input-border bg-bg p-2 text-body text-text focus:border-input-border-focus"
            />
          </label>

          {canUpdate ? (
            <Switch
              checked={direct}
              onCheckedChange={setDirect}
              label={t("Apply directly")}
              description={t("Off: the result is staged as a proposal you review first.")}
            />
          ) : null}
        </div>
      )}
    </Dialog>
  );
}
